import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PhoneInput } from "@/components/ui/phone-input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTikTokTracking } from "../../hooks/useTikTokTracking";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const formSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  instagramHandle: z.string().min(1, "Instagram handle is required").refine(
    (handle) => {
      // Remove @ if present and validate format
      const cleanHandle = handle.replace(/^@/, '');
      return /^[a-zA-Z0-9._]{1,30}$/.test(cleanHandle);
    },
    { message: "Please enter a valid Instagram handle" }
  ),
  email: z.string().email("Please enter a valid email address"),
  countryCode: z.string().min(1, "Country code is required"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  followerCount: z.number().min(1000, "🌟 We love your enthusiasm! To join our creator program, you'll need at least 1,000 followers to ensure the best brand partnerships. Keep growing that amazing community! 🚀"),
  agreementAccepted: z.boolean().refine((val) => val === true, {
    message: "You must agree to the Creator Partnership Agreement",
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface InfluencerRegistrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const InfluencerRegistrationDialog = ({ open, onOpenChange }: InfluencerRegistrationDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trackConversion } = useTikTokTracking();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      instagramHandle: "",
      email: "",
      countryCode: "US",
      phoneNumber: "",
      followerCount: 0,
      agreementAccepted: false,
    },
  });

  const onSubmit = async (data: FormValues) => {
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate submission');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Save to Supabase leads table
      const { error } = await supabase
        .from('leads')
        .insert({
          type: 'influencer',
          email: data.email,
          full_name: data.fullName,
          instagram_handle: data.instagramHandle.replace(/^@/, '').toLowerCase(),
          phone_number: `${data.countryCode}:${data.phoneNumber}`,
          follower_count: data.followerCount,
          metadata: {
            agreement_accepted: data.agreementAccepted,
            submitted_from: 'influencer_registration_dialog'
          }
        });

      if (error) {
        throw error;
      }

      // Send confirmation email
      try {
        const { error: emailError } = await supabase.functions.invoke('send-influencer-confirmation', {
          body: {
            fullName: data.fullName,
            email: data.email,
            instagramHandle: data.instagramHandle,
          }
        });

        if (emailError) {
          console.error("Email sending error:", emailError);
          // Don't throw - we still want to show success even if email fails
        }
      } catch (emailError) {
        console.error("Email sending error:", emailError);
        // Don't throw - we still want to show success even if email fails
      }

      // Send notification to gus@cloutfit.ai
      try {
        const { error: notificationError } = await supabase.functions.invoke('send-signup-notification', {
          body: {
            type: 'influencer',
            data: {
              fullName: data.fullName,
              email: data.email,
              instagramHandle: data.instagramHandle,
              phoneNumber: `${data.countryCode}:${data.phoneNumber}`,
            }
          }
        });

        if (notificationError) {
          console.error("Notification sending error:", notificationError);
          // Don't throw - we still want to show success even if notification fails
        }
      } catch (notificationError) {
        console.error("Notification sending error:", notificationError);
        // Don't throw - we still want to show success even if notification fails
      }

      // Track TikTok conversion using performance-optimized hook
      trackConversion('CompleteRegistration', {
        content_name: 'Creator Program Signup',
        content_category: 'Influencer Registration',
        value: 1,
        currency: 'USD'
      });
      
      toast({
        title: "Registration Successful!",
        description: "Welcome to the Creator Program! We will contact you as soon as your profile link is ready.",
      });
      
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Registration error:", error);
      
      // Check if this is a duplicate email error
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = (error as any).message || '';
        if (errorMessage.includes('unique_email_per_type') || errorMessage.includes('duplicate key')) {
          toast({
            title: "Already registered",
            description: "This email is already registered for the Creator Program.",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      toast({
        title: "Registration Failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const agreementText = `
Creator Partnership Agreement
Effective upon acceptance by the Creator

By completing the form below and clicking I Agree, you ("Creator", "you", or "your") accept the following terms with Asteroid Ventures FZC, the owner and operator of Cloutfit.ai and OutfitAlliance.com ("Cloutfit", "we", "our", or "us").

1. Program Overview
You are joining our Creator Program to help connect fashion inspiration with commerce. By allowing us to feature your public content, we will generate a custom shoppable page that matches outfits from your posts to products available through our brand partners. You will earn a commission on qualifying sales.

2. Content Usage and Ownership
You grant us permission to:
• Embed and display your public social media content (for example, Instagram posts) using official tools such as the embed API; and
• With your prior approval, use selected content in other formats (for example, image uploads or reposts).

Ownership of your posts remains fully with you.
We do not claim any ownership rights over your posts or likeness. Any use of your content under this agreement is limited, revocable, and solely for the purposes described above.

This usage is limited to:
• Cloutfit.ai, OutfitAlliance.com, and associated subdomains; and
• Partner brand websites, solely for the purpose of showcasing your matched outfits and enabling shopping experiences.

3. Earnings and Payment
• You will earn a commission on qualifying sales made through your Cloutfit.ai or OutfitAlliance.com page or tracking links.
• Commission amounts depend on the rates collected from brand partners and may vary.
• Commissions are calculated monthly and paid by the 15th of the following month, once your balance exceeds USD $100 (or the equivalent in your currency).
• Payments are made via Bank Transfer or PayPal.
• You are responsible for any taxes or reporting requirements in your country.

4. Non-Exclusivity
This is a non-exclusive agreement. You are free to collaborate with other brands, platforms, or services at any time. Cloutfit places no restrictions on your current or future partnerships.

5. Term and Termination
• This agreement is effective as soon as you accept it and continues until terminated by either party.
• Either party may terminate this agreement at any time with 7 days' written notice.
• After termination, your creator page will be deactivated and we will stop using your content unless otherwise agreed in writing.

6. Confidentiality and Data
• Any non-public information we share with you should remain confidential.
• Your performance data will be used solely for reporting, payouts, and platform optimization, in line with our Privacy Policy.

7. Independent Relationship
This agreement does not create an employment, agency, or joint venture relationship. You are participating as an independent creator or contractor.

8. Governing Law
This agreement is governed by and interpreted in accordance with the laws of the United Arab Emirates.

By submitting the form below and clicking "I Agree," you confirm that you have read, understood, and accepted these terms.
  `;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] mx-4">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Join the Creator Program</DialogTitle>
          <DialogDescription>
            Complete the form below to join our Creator Program and start earning commissions.
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh] pr-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter your full name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="instagramHandle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instagram Handle *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="@yourusername or yourusername" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="your.email@example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl>
                      <PhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        countryCode={form.watch("countryCode")}
                        onCountryCodeChange={(code) => form.setValue("countryCode", code)}
                        placeholder="Your phone number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="followerCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of Followers *</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="e.g. 5000"
                        value={field.value === 0 || field.value === undefined || field.value === null ? '' : String(field.value)}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, '');
                          field.onChange(raw === '' ? 0 : Number(raw));
                        }}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Creator Partnership Agreement</h3>
                <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-muted/50">
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">
                    {agreementText}
                  </pre>
                </div>
                
                <FormField
                  control={form.control}
                  name="agreementAccepted"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">
                          I agree to the Creator Partnership Agreement *
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Join Creator Program"}
              </Button>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default InfluencerRegistrationDialog;